import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '/@/renderer/api';
import { isMediaNotFoundError, shouldRetryStreamUrl } from '/@/renderer/api/media-not-found';
import { TranscodingConfig, useValidatedServerId } from '/@/renderer/store';
import { logger } from '/@/renderer/utils/logger';
import { toast } from '/@/shared/components/toast/toast';
import { QueueSong } from '/@/shared/types/domain-types';
import { PlayerStatus } from '/@/shared/types/types';

export function useSongUrl(
    song: QueueSong | undefined,
    current: boolean,
    transcode: Partial<TranscodingConfig>,
    options?: {
        /**
         * The server says this song is gone. Return true once it was dealt with
         * (e.g. skipped); it is asked again while it returns false, so a paused
         * entry can still be skipped when playback starts.
         */
        onMediaNotFound?: (song: QueueSong, error: Error, current: boolean) => boolean;
    },
): string | undefined {
    const prior = useRef(['', '']);
    const validatedServerId = useValidatedServerId();
    // No stream URL (and no transcode probe) before this sign-in was checked:
    // a restored queue must not hit the server for a session that may be gone.
    const isSessionReady = Boolean(song?._serverId) && song?._serverId === validatedServerId;
    const shouldReusePrior = Boolean(
        isSessionReady && current && prior.current[0] === song?._uniqueId && prior.current[1],
    );

    const { data: queryStreamUrl, error } = useQuery({
        enabled: isSessionReady && !shouldReusePrior,
        queryFn: () =>
            api.controller.getStreamUrl({
                apiClientProps: { serverId: song!._serverId },
                query: {
                    bitrate: transcode.bitrate,
                    format: transcode.format,
                    id: song!.id,
                    maxSampleRate: transcode.maxSampleRate,
                    transcode: transcode.enabled ?? false,
                },
            }),
        queryKey: [
            song?._serverId,
            'stream-url',
            song?.id,
            shouldReusePrior ? 'reuse-prior' : transcode.bitrate,
            shouldReusePrior ? 'reuse-prior' : transcode.format,
            shouldReusePrior ? 'reuse-prior' : transcode.maxSampleRate,
            shouldReusePrior ? 'reuse-prior' : transcode.enabled,
        ] as const,
        retry: shouldRetryStreamUrl,
        staleTime: 60 * 1000,
    });

    useEffect(() => {
        if (!song?._serverId) {
            prior.current = ['', ''];
            return;
        }

        if (!queryStreamUrl) {
            return;
        }

        // Save resolved URL to avoid restarting current track on transcode setting changes.
        prior.current = [song._uniqueId, queryStreamUrl];
    }, [song?._serverId, song?._uniqueId, queryStreamUrl]);

    useEffect(() => {
        if (!song?._serverId || !isSessionReady) {
            prior.current = ['', ''];
        }
    }, [song?._serverId, isSessionReady]);

    const onMediaNotFound = options?.onMediaNotFound;
    const reportedMissing = useRef<null | string>(null);

    useEffect(() => {
        if (!song || !onMediaNotFound || !isMediaNotFoundError(error)) {
            return;
        }

        if (reportedMissing.current === song._uniqueId) {
            return;
        }

        if (onMediaNotFound(song, error, current)) {
            reportedMissing.current = song._uniqueId;
        }
    }, [current, error, onMediaNotFound, song]);

    if (!isSessionReady) {
        return undefined;
    }

    return shouldReusePrior ? prior.current[1] : queryStreamUrl;
}

/**
 * `onMediaNotFound` for `useSongUrl`: a missing track is final for that queue
 * entry. While the user is playing it, it is skipped with one notice; a paused
 * (e.g. restored) queue keeps it untouched until playback is asked for.
 */
export const useMissingTrackSkipper = (status: PlayerStatus, skipCurrent: () => void) => {
    const { t } = useTranslation();

    return useCallback(
        (song: QueueSong, _error: Error, current: boolean) => {
            if (!current || status !== PlayerStatus.PLAYING) {
                return false;
            }

            logger.warn('Skipping track missing on the server', { id: song.id });
            toast.warn({
                message: t('error.trackNotFoundSkipped', {
                    defaultValue: '"{{title}}" is no longer on the server and was skipped',
                    title: song.name,
                }),
            });
            skipCurrent();
            return true;
        },
        [skipCurrent, status, t],
    );
};

export const getSongUrl = async (
    song: QueueSong,
    transcode: Partial<TranscodingConfig>,
    skipAutoTranscode?: boolean,
    forRenderer?: boolean,
    startTime?: number,
) => {
    const url = await api.controller.getStreamUrl({
        apiClientProps: { serverId: song._serverId },
        query: {
            bitrate: transcode.bitrate,
            container: song.container,
            format: transcode.format,
            forRenderer,
            id: song.id,
            maxSampleRate: transcode.maxSampleRate,
            sampleRate: song.sampleRate,
            skipAutoTranscode,
            startTime,
            transcode: transcode.enabled ?? false,
        },
    });

    return url;
};
